import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

export interface HarvestedContact {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  company_name: string | null;
  location: string | null;
  lead_score: number | null;
  ai_conversion_score: number | null;
  source: string | null;
  status: string | null;
  created_at: string;
  last_activity_at: string | null;
  tags: string[] | null;
  metadata: {
    harvested_from?: string;
    source_type?: string;
    job_title?: string;
    department?: string;
    seniority_level?: string;
    linkedin_url?: string;
    twitter_handle?: string;
    company_linkedin?: string;
    company_industry?: string;
    company_size?: string;
    harvested_at?: string;
    [key: string]: any;
  } | null;
}

export interface CompanyInfo {
  name?: string;
  industry?: string;
  location?: string;
  employee_count?: string;
  founded_year?: number;
  website?: string;
  linkedin_url?: string;
  twitter_handle?: string;
  general_email?: string;
  general_phone?: string;
  description?: string;
}

export interface HarvestProgress {
  stage: string;
  percent: number;
  currentUrl?: string;
  totalUrls?: number;
  completedUrls?: number;
}

interface HarvestOptions {
  sourceType?: 'website' | 'linkedin' | 'twitter' | 'facebook';
  enrichWithLinkedIn?: boolean;
  scrapeDepth?: 'shallow' | 'deep';
}

export function useLeadHarvest() {
  const [isHarvesting, setIsHarvesting] = useState(false);
  const [harvestedContacts, setHarvestedContacts] = useState<HarvestedContact[]>([]);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [progress, setProgress] = useState<HarvestProgress | null>(null);
  const queryClient = useQueryClient();

  const harvestContacts = async (
    companyUrl: string, 
    companyName?: string,
    options: HarvestOptions = {}
  ) => {
    setIsHarvesting(true);
    setHarvestedContacts([]);
    setCompanyInfo(null);
    setProgress({ stage: 'Initializing...', percent: 5 });

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      setProgress({ stage: 'Fetching tenant info...', percent: 10 });

      const { data: profile } = await supabase
        .from('profiles')
        .select('tenant_id')
        .eq('id', user.id)
        .single();

      if (!profile?.tenant_id) throw new Error('No tenant found');

      setProgress({ stage: 'Scraping website...', percent: 20, currentUrl: companyUrl });

      const { data, error } = await supabase.functions.invoke('ai-lead-harvest', {
        body: {
          companyUrl,
          companyName,
          tenantId: profile.tenant_id,
          sourceType: options.sourceType || 'website',
          enrichWithLinkedIn: options.enrichWithLinkedIn ?? true,
          scrapeDepth: options.scrapeDepth || 'shallow',
        },
      });

      if (error) throw error;

      if (data?.error) {
        if (data.error.includes('Rate limit')) {
          toast.error('Rate limit reached. Please try again in a moment.');
        } else {
          toast.error(data.error);
        }
        return { contacts: [], companyInfo: null };
      }

      setProgress({ stage: 'Processing results...', percent: 90 });

      if (data?.success) {
        setHarvestedContacts(data.contacts || []);
        setCompanyInfo(data.company_info || null);
        queryClient.invalidateQueries({ queryKey: ['leads'] });
        setProgress({ stage: 'Complete!', percent: 100 });
        toast.success(data.message || `Harvested ${data.contacts?.length || 0} contacts`);
        return { contacts: data.contacts || [], companyInfo: data.company_info };
      }

      toast.info(data?.message || 'No contacts found');
      return { contacts: [], companyInfo: null };
    } catch (err: any) {
      console.error('Lead harvest error:', err);
      toast.error(err.message || 'Failed to harvest contacts');
      return { contacts: [], companyInfo: null };
    } finally {
      setIsHarvesting(false);
      setTimeout(() => setProgress(null), 2000);
    }
  };

  const harvestBulk = async (
    urls: string[],
    options: Omit<HarvestOptions, 'sourceType'> = {}
  ) => {
    setIsHarvesting(true);
    setHarvestedContacts([]);
    setCompanyInfo(null);

    const limitedUrls = urls.slice(0, 10);
    let allContacts: HarvestedContact[] = [];

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('tenant_id')
        .eq('id', user.id)
        .single();

      if (!profile?.tenant_id) throw new Error('No tenant found');

      for (let i = 0; i < limitedUrls.length; i++) {
        const url = limitedUrls[i];
        setProgress({
          stage: `Processing URL ${i + 1} of ${limitedUrls.length}`,
          percent: Math.round(((i + 0.5) / limitedUrls.length) * 100),
          currentUrl: url,
          totalUrls: limitedUrls.length,
          completedUrls: i,
        });

        try {
          const { data, error } = await supabase.functions.invoke('ai-lead-harvest', {
            body: {
              companyUrl: url,
              tenantId: profile.tenant_id,
              enrichWithLinkedIn: options.enrichWithLinkedIn ?? true,
              scrapeDepth: options.scrapeDepth || 'shallow',
            },
          });

          if (!error && data?.success && data.contacts) {
            allContacts = [...allContacts, ...data.contacts];
            setHarvestedContacts(allContacts);
          }
        } catch (urlError) {
          console.error(`Error harvesting ${url}:`, urlError);
        }

        // Small delay between requests
        if (i < limitedUrls.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      queryClient.invalidateQueries({ queryKey: ['leads'] });
      setProgress({ stage: 'Complete!', percent: 100, totalUrls: limitedUrls.length, completedUrls: limitedUrls.length });
      toast.success(`Harvested ${allContacts.length} contacts from ${limitedUrls.length} URLs`);
      
      return { contacts: allContacts, companyInfo: null };
    } catch (err: any) {
      console.error('Bulk harvest error:', err);
      toast.error(err.message || 'Failed to complete bulk harvest');
      return { contacts: allContacts, companyInfo: null };
    } finally {
      setIsHarvesting(false);
      setTimeout(() => setProgress(null), 2000);
    }
  };

  return {
    isHarvesting,
    harvestedContacts,
    companyInfo,
    progress,
    harvestContacts,
    harvestBulk,
  };
}
